import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { pixelAtTime } from '../../timelineGeometry';
import css from './TimestampNeedle.module.scss';

type TimestampNeedleProps = {
  timelineWidth: number;
  duration: number;
  store?: VideoPlayerStore;
  isZooming?: boolean;
};

export const TimestampNeedle = observer(
  ({
    timelineWidth,
    duration,
    store = videoPlayerStore,
    isZooming = false,
  }: TimestampNeedleProps) => {
    const [position, setPosition] = useState(0);
    const animationRef = useRef<number | null>(null);
    const lastUpdateTimeRef = useRef<number>(0);

    const updatePosition = () => {
      if (!duration || !timelineWidth) return;

      // Shared with the cells rather than spelled out again here: the needle and
      // the filmstrip drifting apart is exactly the bug `timelineGeometry` exists
      // to have fixed once.
      const newPosition = pixelAtTime(store.currentTime, { timelineWidth, duration });

      setPosition(newPosition);

      // Schedule next update
      animationRef.current = requestAnimationFrame(updatePosition);
    };

    useEffect(() => {
      // Start the animation loop
      animationRef.current = requestAnimationFrame(updatePosition);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }, [timelineWidth, duration, store.currentTime]);

    return (
      <div
        className={css.timestampNeedle}
        style={{
          transform: `translateX(${position}px)`,
          opacity: isZooming ? 0 : 1,
        }}
      />
    );
  },
);
